import { useEffect, useState } from "react";
import {
  isCrossOriginFrameError,
  isIgnorableWindowError,
  isOpaqueScriptError,
} from "@/utils/ignorableErrors";

export function ErrorDisplay() {
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    // Load persisted errors from sessionStorage
    try {
      const stored = sessionStorage.getItem("__app_errors");
      if (stored) {
        const parsed = JSON.parse(stored);
        setErrors(
          parsed
            .map((e: { msg?: string }) => e.msg || String(e))
            // Entries saved before opaque "Script error." was filtered out.
            .filter(
              (msg: string) =>
                !isOpaqueScriptError({ message: msg.replace(/^Error: /, "") }),
            ),
        );
      }
    } catch (e) {
      console.error("Failed to load stored errors:", e);
    }

    // Listen for new errors
    const handleError = (event: ErrorEvent) => {
      if (isIgnorableWindowError(event)) {
        event.preventDefault();
        return;
      }
      const msg = `${event.error?.message || event.message}`;
      setErrors((prev) => [...prev.slice(-9), msg]);
      event.preventDefault();
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isCrossOriginFrameError(event.reason)) {
        event.preventDefault();
        return;
      }
      const msg = `Promise: ${event.reason?.message || event.reason}`;
      setErrors((prev) => [...prev.slice(-9), msg]);
      event.preventDefault();
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  if (errors.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-f1red/90 border-t border-f1red text-fg p-3 text-xs font-mono z-50 max-h-32 overflow-y-auto">
      <div className="font-bold mb-2 flex justify-between">
        <span>Debug: {errors.length} Error(s)</span>
        <button
          onClick={() => {
            setErrors([]);
            try {
              sessionStorage.removeItem("__app_errors");
            } catch {
              // Ignore errors when clearing storage
            }
          }}
          className="text-fg/70 hover:text-fg"
        >
          ✕
        </button>
      </div>
      {errors.map((err, i) => (
        <div key={i} className="truncate text-fg/80">
          {i + 1}. {err}
        </div>
      ))}
    </div>
  );
}
